import React, { useState, useEffect, useMemo } from 'react';
import { toDatetimeLocal, coachLocalToUtcIso } from '../utils/timezoneUtils';
import { XMarkIcon, ClockIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import SlotPicker from './SlotPicker';
import { startOfWeek, addDays } from 'date-fns';
import { EventTypeOption } from './EventTypePicker';
import CoachCalendarView, { CalendarEvent } from './CoachCalendarView';
import { config } from '../config/environment';
import { getServerConfig } from '../config/serverConfig';
import { registrationService } from '../services/registrationService';
import { setAppointmentPricing, getPaymentSettings } from '../services/paymentService';
import { AppointmentType, APPOINTMENT_TYPES, getAppointmentTypeLabel } from '../constants/appointmentTypes';

interface ScheduleClient {
  client_id: string;
  name: string;
  email: string;
  /** Coach-client relationship ID (integer PK). Required for recurring series. */
  coachClientId?: string;
  ai_consent?: boolean | null;
}


interface CalendlyEventType {
  uri: string;
  name: string;
  duration: number;
  scheduling_url: string;
  video_configured: boolean;
  video_type: string | null;
}

/** When set, the modal runs in reschedule mode: same type/slot picking flow,
 *  but confirming PATCHes the existing appointment instead of creating one. */
export interface RescheduleInfo {
  appointmentId: string;
  meetingName: string;
  /** Current scheduled time (ISO) — shown so the coach sees what they're moving. */
  scheduledAt: string;
  durationMinutes: number;
  /** For recurring series appointments: which occurrences to move. */
  recurringScope?: 'this_only' | 'this_and_future';
}

interface ScheduleSessionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSessionCreated: (meetLink?: string) => void;
  client: ScheduleClient | null;
  reschedule?: RescheduleInfo | null;
  coachId: string;
  authToken: string;
  schedulingType: 'google' | 'calendly' | 'none';
  /** Existing calendar events to show as background on the calendar. */
  existingEvents?: CalendarEvent[];
  /** Called when coach clicks the trash icon on an existing appointment. */
  onCancelAppointment?: (appointmentId: string) => void;
  /**
   * The coach's IANA timezone (e.g. "America/New_York"), used as a fallback
   * query parameter when the coach's DB timezone is still the server default 'UTC'.
   * Defaults to the browser's detected timezone.
   */
  coachTimezone?: string;
  coachCurrency?: string;
  // Calendly-specific
  calendlyEventTypes: CalendlyEventType[];
  calendlySelectedUris: string[];
  calendlyEventTypesLoading: boolean;
  onLoadCalendlyEventTypes: () => void;
}

function formatTime(iso: string): string {
  return new Date(iso).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
}

// ── Google scheduling panel ─────────────────────────────────────────────────

type RightPanel = 'types' | 'confirm';

const CADENCE_OPTIONS = [
  { value: 'weekly',   label: 'Weekly' },
  { value: 'biweekly', label: 'Every 2 weeks' },
  { value: 'monthly',  label: 'Monthly' },
] as const;

type Cadence = 'weekly' | 'biweekly' | 'monthly';


interface Currency { code: string; name: string; }

interface GooglePanelProps {
  client: ScheduleClient;
  coachId: string;
  authToken: string;
  eventTypes: EventTypeOption[];
  eventTypesLoading: boolean;
  existingEvents: CalendarEvent[];
  currencies: Currency[];
  coachCurrency?: string;
  coachTimezone: string;
  onSessionCreated: (meetLink?: string) => void;
  onClose: () => void;
  onCancelAppointment?: (appointmentId: string) => void;
  reschedule?: RescheduleInfo | null;
}

const GoogleSchedulePanel: React.FC<GooglePanelProps> = ({
  client,
  coachId,
  authToken,
  eventTypes,
  eventTypesLoading,
  existingEvents,
  coachTimezone,
  onSessionCreated,
  onClose,
  onCancelAppointment,
  reschedule,
  currencies,
  coachCurrency,
}) => {
  const [allowPastBookings, setAllowPastBookings] = useState(false);
  const [rightPanel, setRightPanel] = useState<RightPanel>('types');
  const [selectedEventType, setSelectedEventType] = useState<EventTypeOption | null>(null);
  const [selectedSlot, setSelectedSlot] = useState<string | null>(null); // ISO

  // Track which week the calendar is showing so we can re-fetch slots on navigation.
  // In reschedule mode, open on the week of the session being moved.
  const [calendarDate, setCalendarDate] = useState(() =>
    reschedule ? new Date(reschedule.scheduledAt) : new Date()
  );

  // Fetched available slots for the displayed week
  const [availableSlots, setAvailableSlots] = useState<string[]>([]);
  const [slotsLoading, setSlotsLoading] = useState(false);

  // Confirm step state
  const [meetingName, setMeetingName] = useState('');
  const [sessionRate, setSessionRate] = useState('');
  const [sessionCurrency, setSessionCurrency] = useState(coachCurrency?.toLowerCase() || 'usd');
  const [appointmentType, setAppointmentType] = useState<AppointmentType>('billable');
  const [barterNote, setBarterNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  // Editable datetime in the confirm panel (YYYY-MM-DDTHH:MM in coach timezone).
  // Pre-filled from the selected slot via toDatetimeLocal; converted back to UTC on submit via coachLocalToUtcIso.
  const [localDatetime, setLocalDatetime] = useState('');

  // Package enrollment selection (optional, only shown when client has matching active packages)
  interface PackageOption { enrollment_id: string; package_name: string; sessions_used: number; sessions_total: number | null; exhausted: boolean; }
  const [packageOptions, setPackageOptions] = useState<PackageOption[]>([]);
  const [selectedEnrollmentId, setSelectedEnrollmentId] = useState<string>('');

  // Recurring options
  const [isRecurring, setIsRecurring] = useState(false);
  const [recurringCadence, setRecurringCadence] = useState<Cadence>('weekly');
  const [recurringCount, setRecurringCount] = useState(6);

  // Success state — set after booking; kept open so coach can book another
  const [bookedInfo, setBookedInfo] = useState<{ name: string; slot: string; duration: number; recurringCount?: number; meetLink?: string | null; rescheduled?: boolean } | null>(null);

  // Pre-fill rate on mount
  useEffect(() => {
    getServerConfig().then(sc => setAllowPastBookings(sc.allow_past_bookings));
  }, []);

  // Reschedule mode: pre-select the session type matching the session being moved
  // (by duration) so availability slots load immediately without an extra click.
  useEffect(() => {
    if (!reschedule || selectedEventType || eventTypes.length === 0) return;
    const match = eventTypes.find(et => et.duration_minutes === reschedule.durationMinutes) ?? eventTypes[0];
    setSelectedEventType(match);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reschedule, eventTypes]);

  useEffect(() => {
    getPaymentSettings()
      .then(ps => {
        if (ps.session_rate_cents && ps.session_rate_cents > 0) {
          setSessionRate(String(ps.session_rate_cents / 100));
          if ((ps as any).currency) setSessionCurrency((ps as any).currency);
        }
      })
      .catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch available slots whenever event type or calendar week changes
  useEffect(() => {
    if (!selectedEventType?.id) { setAvailableSlots([]); return; }

    const weekStart = startOfWeek(calendarDate, { weekStartsOn: 0 });
    const weekEnd = addDays(weekStart, 7);
    weekStart.setHours(0, 0, 0, 0);
    weekEnd.setHours(23, 59, 59, 999);

    const params = new URLSearchParams({
      event_type_id: selectedEventType.id,
      date_from: weekStart.toISOString(),
      date_to: weekEnd.toISOString(),
      // Pass coach timezone so the backend uses the correct timezone for working-hours
      // checks even if the coach hasn't explicitly saved their timezone preference yet.
      tz: coachTimezone,
      ...(allowPastBookings ? { bypass_past: 'true' } : {}),
    });

    setSlotsLoading(true);
    setAvailableSlots([]);
    fetch(`${config.apiBaseUrl}/book/${coachId}/slots?${params}`, {
      headers: { Authorization: `Bearer ${authToken}` },
    })
      .then(r => r.ok ? r.json() : { slots: [] })
      .then(data => setAvailableSlots(data.slots ?? []))
      .catch(() => {})
      .finally(() => setSlotsLoading(false));
  }, [selectedEventType?.id, calendarDate, coachId, authToken, coachTimezone, allowPastBookings]);

  // Fetch active package enrollments only after a date+time slot is selected.
  // Pass session_date so the backend can enforce enrollment date range and monthly caps.
  useEffect(() => {
    setPackageOptions([]);
    setSelectedEnrollmentId('');
    if (!selectedEventType?.id || !client.coachClientId || !localDatetime) return;
    const sessionDate = localDatetime.split('T')[0]; // YYYY-MM-DD from local datetime
    fetch(
      `${config.apiBaseUrl}/packages/coach-client/${client.coachClientId}/active-for-event-type?event_type_id=${selectedEventType.id}&session_date=${sessionDate}`,
      { headers: { Authorization: `Bearer ${authToken}` } },
    )
      .then(r => r.ok ? r.json() : [])
      .then((data: PackageOption[]) => setPackageOptions(data))
      .catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEventType?.id, client.coachClientId, authToken, localDatetime]);

  // Scroll calendar to the earliest available slot (avoids slots appearing off-screen when
  // coach timezone differs from browser timezone — e.g. UTC slots shown in US Pacific time)
  const scrollToTime = useMemo(() => {
    if (availableSlots.length === 0) return new Date(1970, 1, 1, 8, 0, 0);
    const earliest = availableSlots.reduce<Date>((min, iso) => {
      const d = new Date(iso);
      return d < min ? d : min;
    }, new Date(availableSlots[0]));
    // react-big-calendar extracts hours/minutes from this date regardless of year
    return new Date(1970, 1, 1, earliest.getHours(), earliest.getMinutes(), 0);
  }, [availableSlots]);

  // Build calendar events: existing sessions + green available slots
  const calendarEvents = useMemo<CalendarEvent[]>(() => {
    const slotEvents: CalendarEvent[] = availableSlots.map(iso => ({
      id: `avail-${iso}`,
      title: selectedEventType ? `Available · ${selectedEventType.duration_minutes} min` : 'Available',
      start: new Date(iso),
      end: new Date(new Date(iso).getTime() + (selectedEventType?.duration_minutes ?? 60) * 60_000),
      allDay: false,
      resource: { type: 'available' },
    }));

    // Highlight the selected slot differently
    if (selectedSlot) {
      const idx = slotEvents.findIndex(e => e.id === `avail-${selectedSlot}`);
      if (idx >= 0) {
        slotEvents[idx] = {
          ...slotEvents[idx],
          title: `Selected · ${selectedEventType?.duration_minutes ?? 60} min`,
          resource: { type: 'selected' },
        };
      }
    }

    return [...existingEvents, ...slotEvents];
  }, [existingEvents, availableSlots, selectedSlot, selectedEventType]);

  const handleSelectEventType = (et: EventTypeOption) => {
    setSelectedEventType(et);
    setSelectedSlot(null);
    setRightPanel('types'); // stay on types until a slot is clicked
  };

  const handleCalendarEventClick = (event: CalendarEvent) => {
    if (event.resource?.type !== 'available' && event.resource?.type !== 'selected') return;
    const iso = event.start.toISOString();
    const match = availableSlots.find(s => new Date(s).getTime() === event.start.getTime());
    const slotIso = match ?? iso;
    setSelectedSlot(slotIso);
    setLocalDatetime(toDatetimeLocal(slotIso, coachTimezone));
    setMeetingName(selectedEventType?.name ?? '');
    setIsRecurring(false);
    setRightPanel('confirm');
  };

  const handleCustomTime = () => {
    setSelectedSlot(null);
    setLocalDatetime('');
    setMeetingName(selectedEventType?.name ?? '');
    setIsRecurring(false);
    setRightPanel('confirm');
  };

  const handleConfirm = async () => {
    if (!selectedEventType || !localDatetime) return;
    setSubmitting(true);
    setSubmitError('');

    const scheduledAt = coachLocalToUtcIso(localDatetime, coachTimezone);

    // Reschedule mode: move the existing appointment instead of creating one.
    // The backend also updates the Google Calendar event and Zoom meeting.
    if (reschedule) {
      try {
        const res = await fetch(`${config.apiBaseUrl}/coaches/sessions/${reschedule.appointmentId}/reschedule`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
          body: JSON.stringify({
            scheduled_at: scheduledAt,
            duration_minutes: selectedEventType.duration_minutes,
            ...(reschedule.recurringScope ? { scope: reschedule.recurringScope } : {}),
          }),
        });
        if (res.ok) {
          onSessionCreated();
          setBookedInfo({
            name: reschedule.meetingName,
            slot: scheduledAt,
            duration: selectedEventType.duration_minutes,
            rescheduled: true,
          });
        } else {
          const data = await res.json().catch(() => ({}));
          setSubmitError((data as any).detail || 'Failed to reschedule session');
        }
      } catch {
        setSubmitError('Failed to reschedule session');
      } finally {
        setSubmitting(false);
      }
      return;
    }

    try {
      if (appointmentType === 'barter' && !barterNote.trim()) {
        setSubmitError('A barter note is required for barter sessions.');
        setSubmitting(false);
        return;
      }

      if (isRecurring) {
        if (!client.coachClientId) {
          setSubmitError('Cannot create recurring series: missing relationship ID.');
          setSubmitting(false);
          return;
        }
        const token = localStorage.getItem('authToken') ?? '';
        const res = await fetch(`${config.apiBaseUrl}/recurring-series`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({
            coach_client_id: parseInt(client.coachClientId, 10),
            meeting_name: meetingName || selectedEventType.name,
            start: scheduledAt,
            cadence: recurringCadence,
            session_count: recurringCount,
            duration_minutes: selectedEventType.duration_minutes,
            appointment_type: appointmentType,
            barter_note: appointmentType === 'barter' ? barterNote.trim() : null,
            package_enrollment_id: selectedEnrollmentId || null,
            event_type_id: selectedEventType.id || null,
          }),
        });
        const data = await res.json() as { series_id?: string; appointments?: unknown[]; meet_link?: string | null; detail?: string };
        if (!res.ok) { setSubmitError(data.detail || 'Failed to create recurring series.'); return; }
        onSessionCreated(data.meet_link ?? undefined);
        setBookedInfo({
          name: meetingName || selectedEventType.name,
          slot: scheduledAt,
          duration: selectedEventType.duration_minutes,
          recurringCount,
          meetLink: data.meet_link ?? null,
        });
      } else {
        const res = await registrationService.createSession({
          coach_id: coachId,
          client_id: client.client_id,
          meeting_name: meetingName || selectedEventType.name,
          scheduled_at: scheduledAt,
          duration_minutes: selectedEventType.duration_minutes,
          meeting_url: '',
          appointment_type: appointmentType,
          barter_note: appointmentType === 'barter' ? barterNote.trim() : undefined,
          event_type_id: selectedEventType.id,
          package_enrollment_id: selectedEnrollmentId || undefined,
        });
        if (res.success) {
          const appointmentId = res.data?.appointment?.id;
          const rateVal = parseFloat(sessionRate);
          if (appointmentId && appointmentType === 'billable' && !isNaN(rateVal) && rateVal > 0) {
            try { await setAppointmentPricing(appointmentId, rateVal, sessionCurrency); } catch { /* non-fatal */ }
          }
          onSessionCreated(res.data?.appointment?.meeting_url);
          setBookedInfo({
            name: meetingName || selectedEventType.name,
            slot: scheduledAt,
            duration: selectedEventType.duration_minutes,
          });
        } else {
          setSubmitError(res.error || 'Failed to create session');
        }
      }
    } catch {
      setSubmitError('Failed to create session');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex-1 flex overflow-hidden">

      {/* ── Left: Weekly calendar ── */}
      <div className="flex-1 min-w-0 p-4 overflow-hidden flex flex-col">
        {!selectedEventType && (
          <p className="text-xs text-gray-500 text-center pb-2">
            Select a session type on the right to see available slots
          </p>
        )}
        {slotsLoading && (
          <p className="text-xs text-primary-400 text-center pb-2 flex items-center justify-center gap-1.5">
            <span className="inline-block w-3 h-3 border-2 border-primary-400 border-t-transparent rounded-full animate-spin" />
            Loading availability…
          </p>
        )}
        <CoachCalendarView
          events={calendarEvents}
          onSelectSlot={() => {}}
          onSelectEvent={handleCalendarEventClick}
          onDateChange={setCalendarDate}
          lockedView="week"
          defaultView="week"
          scrollToTime={scrollToTime}
          defaultDate={calendarDate}
          onDeleteEvent={ev => ev.resource?.appointmentId && onCancelAppointment?.(ev.resource.appointmentId)}
        />
      </div>

      {/* ── Right: Session type list, Confirm, or Booked ── */}
      <div className="w-72 flex-shrink-0 border-l border-dark-border flex flex-col overflow-y-auto bg-dark-sidebar">

        {/* Success state — stay open so coach can book another session */}
        {bookedInfo && (
          <div className="p-5 space-y-4">
            <div className="p-4 bg-green-900/30 border border-green-700/40 rounded-xl text-center">
              <CheckCircleIcon className="w-8 h-8 text-green-400 mx-auto mb-2" />
              <p className="text-sm font-semibold text-green-400">
                {bookedInfo.rescheduled ? 'Session rescheduled!' : bookedInfo.recurringCount ? `${bookedInfo.recurringCount} sessions scheduled!` : 'Session booked!'}
              </p>
              <p className="text-xs text-gray-300 mt-1 font-medium">{bookedInfo.name}</p>
              <p className="text-xs text-gray-500 mt-0.5">
                {bookedInfo.recurringCount
                  ? `Starting ${new Date(bookedInfo.slot).toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' })}`
                  : `${new Date(bookedInfo.slot).toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' })} · ${formatTime(bookedInfo.slot)}`}
                {' · '}{bookedInfo.duration} min
              </p>
              {bookedInfo.meetLink && (
                <p className="text-xs text-green-400 mt-1">
                  Meet link: <a href={bookedInfo.meetLink} target="_blank" rel="noreferrer" className="underline">{bookedInfo.meetLink}</a>
                </p>
              )}
            </div>
            {!bookedInfo.rescheduled && (
            <button
              onClick={() => {
                setBookedInfo(null);
                setSelectedSlot(null);
                setLocalDatetime('');
                setRightPanel('types');
                setMeetingName('');
                setAppointmentType('billable');
                setBarterNote('');
                setIsRecurring(false);
              }}
              className="w-full py-2.5 rounded-xl text-sm font-semibold text-white bg-primary-600 hover:bg-primary-700 transition-colors"
            >
              Schedule another session
            </button>
            )}
            <button
              onClick={onClose}
              className="w-full py-2 rounded-xl text-sm font-medium text-gray-400 hover:text-white border border-dark-border hover:border-gray-500 transition-colors"
            >
              Done
            </button>
          </div>
        )}

        {/* Session type list */}
        {!bookedInfo && rightPanel === 'types' && (
          <div className="p-5">
            {reschedule && (
              <div className="mb-4 p-3 bg-amber-900/20 border border-amber-700/40 rounded-xl">
                <p className="text-xs font-semibold text-amber-400">Rescheduling</p>
                <p className="text-xs text-gray-300 mt-0.5">{reschedule.meetingName}</p>
                <p className="text-xs text-gray-500 mt-0.5">
                  Currently {new Date(reschedule.scheduledAt).toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' })}
                  {' · '}{formatTime(reschedule.scheduledAt)}
                </p>
              </div>
            )}
            <p className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-3">Session Type</p>
            {eventTypesLoading ? (
              <div className="animate-spin w-4 h-4 border-2 border-primary-500 border-t-transparent rounded-full" />
            ) : eventTypes.length === 0 ? (
              <p className="text-sm text-gray-400">No session types configured. Add them in Settings → Scheduling.</p>
            ) : (
              <div className="space-y-2">
                {eventTypes.map((et, i) => {
                  const isActive = selectedEventType?.id === et.id;
                  return (
                    <button
                      key={et.id ?? i}
                      onClick={() => handleSelectEventType(et)}
                      className={[
                        'w-full text-left rounded-xl px-4 py-3 border transition-colors',
                        isActive
                          ? 'bg-primary-900/40 border-primary-600 text-white'
                          : 'bg-dark-card border-dark-border hover:border-primary-500 text-gray-200',
                      ].join(' ')}
                    >
                      <p className="text-sm font-medium">{et.name}</p>
                      <p className="text-xs text-gray-400 mt-0.5 flex items-center gap-1">
                        <ClockIcon className="w-3 h-3" />
                        {et.duration_minutes} min
                        {et.description && <span className="ml-1 text-gray-500">· {et.description}</span>}
                      </p>
                    </button>
                  );
                })}
              </div>
            )}

            {selectedEventType && (
              <div className="mt-4 space-y-2">
                <p className="text-xs text-gray-500">
                  Click a <span className="text-green-400">green slot</span> on the calendar, or
                </p>
                <button
                  type="button"
                  onClick={handleCustomTime}
                  className="w-full py-2 rounded-lg text-xs font-medium text-primary-400 border border-primary-500/40 hover:border-primary-500 hover:text-primary-300 transition-colors"
                >
                  Set custom date & time →
                </button>
              </div>
            )}
          </div>
        )}

        {/* Confirm form */}
        {!bookedInfo && rightPanel === 'confirm' && selectedEventType && (
          <div className="p-5 space-y-4">
            {/* Date & time picker */}
            <div>
              <label className="block text-xs font-medium text-gray-400 mb-1">Date & Time</label>
              <input
                type="datetime-local"
                value={localDatetime}
                min={allowPastBookings ? undefined : toDatetimeLocal(new Date().toISOString(), coachTimezone)}
                onChange={e => setLocalDatetime(e.target.value)}
                className="w-full border border-dark-border rounded-lg p-2 text-sm bg-dark-canvas text-gray-200 focus:ring-1 focus:ring-primary-500 focus:outline-none"
              />
              <p className="text-xs text-gray-600 mt-1">{selectedEventType.duration_minutes} min · {selectedEventType.name}</p>
            </div>

            <button
              onClick={() => { setRightPanel('types'); setSelectedSlot(null); setLocalDatetime(''); }}
              className="text-xs text-gray-400 hover:text-gray-200 transition-colors"
            >
              ← Back to session types
            </button>

            {/* Reschedule summary — name/type/rate are unchanged, only time moves */}
            {reschedule && (
              <div className="p-3 bg-amber-900/20 border border-amber-700/40 rounded-xl">
                <p className="text-xs font-semibold text-amber-400">Rescheduling {reschedule.meetingName}</p>
                <p className="text-xs text-gray-500 mt-0.5">
                  From {new Date(reschedule.scheduledAt).toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' })}
                  {' · '}{formatTime(reschedule.scheduledAt)}
                </p>
                <p className="text-xs text-gray-500 mt-1.5">
                  The client will be notified. The calendar event and Zoom meeting update automatically — the join link stays the same.
                </p>
              </div>
            )}

            {/* Session name */}
            {!reschedule && (
            <div>
              <label className="block text-xs font-medium text-gray-400 mb-1">Session Name</label>
              <input
                type="text"
                value={meetingName}
                onChange={e => setMeetingName(e.target.value)}
                placeholder={selectedEventType.name}
                className="w-full border border-dark-border rounded-lg p-2 text-sm bg-dark-canvas text-gray-200 placeholder-dark-muted focus:ring-1 focus:ring-primary-500 focus:outline-none"
              />
            </div>
            )}

            {/* Package enrollment — shown when client has active packages for this session type */}
            {packageOptions.length > 0 && (
              <div>
                <label className="block text-xs font-medium text-gray-400 mb-1">
                  Apply to package <span className="text-dark-muted font-normal">(optional)</span>
                </label>
                <select
                  value={selectedEnrollmentId}
                  onChange={e => setSelectedEnrollmentId(e.target.value)}
                  className="w-full border border-dark-border rounded-lg p-2 text-sm bg-dark-canvas text-gray-200 focus:ring-1 focus:ring-primary-500 focus:outline-none"
                >
                  <option value="">— No package —</option>
                  {packageOptions.map(p => (
                    <option key={p.enrollment_id} value={p.enrollment_id} disabled={p.exhausted}>
                      {p.package_name}
                      {p.sessions_total != null
                        ? ` (${p.sessions_used}/${p.sessions_total} used${p.exhausted ? ' — full' : ''})`
                        : ` (${p.sessions_used} used)`}
                    </option>
                  ))}
                </select>
                {selectedEnrollmentId && packageOptions.find(p => p.enrollment_id === selectedEnrollmentId)?.exhausted && (
                  <p className="mt-1 text-xs text-red-400">This package is fully used. Select a different option or leave blank.</p>
                )}
              </div>
            )}

            {/* Appointment type, rate, recurring — not editable during reschedule */}
            {!reschedule && (<>
            <div>
              <label className="block text-xs font-medium text-gray-400 mb-1">Session Type</label>
              <select
                value={appointmentType}
                onChange={e => setAppointmentType(e.target.value as AppointmentType)}
                className="w-full border border-dark-border rounded-lg p-2 text-sm bg-dark-canvas text-gray-200 focus:ring-1 focus:ring-primary-500 focus:outline-none"
              >
                {APPOINTMENT_TYPES.map(t => (
                  <option key={t} value={t}>{getAppointmentTypeLabel(t)}</option>
                ))}
              </select>
            </div>

            {/* Barter note — required when type is barter */}
            {appointmentType === 'barter' && (
              <div>
                <label className="block text-xs font-medium text-gray-400 mb-1">
                  Barter Note <span className="text-red-400">*</span>
                </label>
                <textarea
                  value={barterNote}
                  onChange={e => setBarterNote(e.target.value)}
                  placeholder="Describe the exchange (e.g. logo design for 3 sessions)"
                  rows={2}
                  className="w-full border border-dark-border rounded-lg p-2 text-sm bg-dark-canvas text-gray-200 placeholder-dark-muted focus:ring-1 focus:ring-primary-500 focus:outline-none resize-none"
                />
              </div>
            )}

            {/* Rate — only for billable */}
            {appointmentType === 'billable' && (
            <div>
              <label className="block text-xs font-medium text-gray-400 mb-1">
                Rate <span className="text-dark-muted font-normal">(optional)</span>
              </label>
              <div className="flex gap-2">
                <input
                  type="number" min="0" step="0.01" value={sessionRate}
                  onChange={e => setSessionRate(e.target.value)} placeholder="150.00"
                  className="flex-1 border border-dark-border rounded-lg p-2 text-sm bg-dark-canvas text-gray-200 placeholder-dark-muted focus:ring-1 focus:ring-primary-500 focus:outline-none"
                />
                <select value={sessionCurrency} onChange={e => setSessionCurrency(e.target.value)}
                  className="border border-dark-border rounded-lg p-2 text-sm bg-dark-canvas text-gray-200 focus:outline-none">
                  {currencies.map(c => (
                    <option key={c.code} value={c.code}>{c.name}</option>
                  ))}
                </select>
              </div>
            </div>
            )}

            {/* Recurring checkbox */}
            <div className="border-t border-dark-border pt-3">
              <label className="flex items-center gap-2.5 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={isRecurring}
                  onChange={e => setIsRecurring(e.target.checked)}
                  className="w-4 h-4 rounded border-dark-border accent-primary-500"
                />
                <span className="text-sm text-gray-300">Repeat this session</span>
              </label>

              {isRecurring && (
                <div className="mt-3 space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-400 mb-1">Cadence</label>
                      <select
                        value={recurringCadence}
                        onChange={e => setRecurringCadence(e.target.value as Cadence)}
                        className="w-full border border-dark-border rounded-lg p-2 text-sm bg-dark-canvas text-gray-200 focus:ring-1 focus:ring-primary-500 focus:outline-none"
                      >
                        {CADENCE_OPTIONS.map(o => (
                          <option key={o.value} value={o.value}>{o.label}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-400 mb-1">Sessions</label>
                      <input
                        type="number"
                        min={2}
                        max={20}
                        value={recurringCount}
                        onChange={e => setRecurringCount(parseInt(e.target.value, 10))}
                        className="w-full border border-dark-border rounded-lg p-2 text-sm bg-dark-canvas text-gray-200 focus:ring-1 focus:ring-primary-500 focus:outline-none"
                      />
                    </div>
                  </div>
                  {(() => {
                    if (!selectedEnrollmentId) return null;
                    const pkg = packageOptions.find(p => p.enrollment_id === selectedEnrollmentId);
                    if (!pkg || pkg.sessions_total == null) return null;
                    const remaining = pkg.sessions_total - pkg.sessions_used;
                    if (recurringCount <= remaining) {
                      return <p className="text-xs text-green-400">{remaining - recurringCount} session{remaining - recurringCount !== 1 ? 's' : ''} remaining in package after this series.</p>;
                    }
                    return <p className="text-xs text-red-400">Package only has {remaining} session{remaining !== 1 ? 's' : ''} remaining. Reduce the count or remove the package.</p>;
                  })()}
                  <p className="text-xs text-gray-600">All sessions share one Google Meet link.</p>
                </div>
              )}
            </div>
            </>)}

            {submitError && (
              <p className="text-xs text-red-400 bg-red-900/20 rounded-lg px-3 py-2">{submitError}</p>
            )}

            <button
              onClick={handleConfirm}
              disabled={submitting || !localDatetime || !!(selectedEnrollmentId && packageOptions.find(p => p.enrollment_id === selectedEnrollmentId)?.exhausted) || (() => {
                if (!isRecurring || !selectedEnrollmentId) return false;
                const pkg = packageOptions.find(p => p.enrollment_id === selectedEnrollmentId);
                if (!pkg || pkg.sessions_total == null) return false;
                return recurringCount > (pkg.sessions_total - pkg.sessions_used);
              })()}
              className="w-full py-2.5 rounded-xl text-sm font-semibold text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              {submitting
                ? (reschedule ? 'Rescheduling…' : 'Creating…')
                : reschedule
                  ? 'Reschedule Session'
                  : isRecurring
                    ? `Create ${recurringCount} Sessions`
                    : 'Create Session'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// ── Main modal shell ─────────────────────────────────────────────────────────

const ScheduleSessionModal: React.FC<ScheduleSessionModalProps> = ({
  isOpen,
  onClose,
  onSessionCreated,
  client,
  reschedule = null,
  coachId,
  authToken,
  schedulingType,
  existingEvents = [],
  onCancelAppointment,
  coachTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
  coachCurrency,
  calendlyEventTypes,
  calendlySelectedUris,
  calendlyEventTypesLoading,
  onLoadCalendlyEventTypes,
}) => {
  const [googleEventTypes, setGoogleEventTypes] = useState<EventTypeOption[]>([]);
  const [googleEventTypesLoading, setGoogleEventTypesLoading] = useState(false);
  const [currencies, setCurrencies] = useState<Currency[]>([{ code: 'usd', name: 'USD' }]);
  const [calendlyStep, setCalendlyStep] = useState<'types' | 'slots' | 'confirm'>('types');
  const [selectedCalendlyEt, setSelectedCalendlyEt] = useState<EventTypeOption | null>(null);
  const [calendlyLocalDatetime, setCalendlyLocalDatetime] = useState('');
  const [calendlyMeetingName, setCalendlyMeetingName] = useState('');
  const [calendlyRate, setCalendlyRate] = useState('');
  const [calendlyCurrency, setCalendlyCurrency] = useState(coachCurrency?.toLowerCase() || 'usd');
  const [calendlySubmitting, setCalendlySubmitting] = useState(false);
  const [calendlySubmitError, setCalendlySubmitError] = useState('');
  const [calendlyBookedInfo, setCalendlyBookedInfo] = useState<{ name: string; slot: string; duration: number; meetUrl?: string } | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    setCalendlyStep('types');
    setSelectedCalendlyEt(null);
    setCalendlyLocalDatetime('');
    setCalendlyMeetingName('');
    setCalendlySubmitting(false);
    setCalendlySubmitError('');
    setCalendlyBookedInfo(null);

    fetch(`${config.apiBaseUrl}/countries`)
      .then(r => r.ok ? r.json() : [])
      .then((countries: any[]) => {
        const seen = new Set<string>();
        const unique = countries
          .filter((c: any) => { if (seen.has(c.currency_code)) return false; seen.add(c.currency_code); return true; })
          .map((c: any) => ({ code: c.currency_code.toLowerCase(), name: c.currency_code.toUpperCase() }))
          .sort((a, b) => a.name.localeCompare(b.name));
        if (unique.length > 0) setCurrencies(unique);
      })
      .catch(() => {});

    if (schedulingType === 'google') {
      setGoogleEventTypesLoading(true);
      fetch(`${config.apiBaseUrl}/event-types`, {
        headers: { Authorization: `Bearer ${authToken}` },
      })
        .then(r => r.ok ? r.json() : [])
        .then((ets: any[]) => setGoogleEventTypes(
          ets.filter((et: any) => et.is_active).map((et: any) => ({
            id: et.id,
            name: et.name,
            duration_minutes: et.duration_minutes,
            description: et.description,
          }))
        ))
        .catch(() => setGoogleEventTypes([]))
        .finally(() => setGoogleEventTypesLoading(false));
    }

    if (schedulingType === 'calendly' && calendlyEventTypes.length === 0) {
      onLoadCalendlyEventTypes();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, schedulingType]);

  if (!isOpen || !client) return null;

  const handleCalendlyConfirm = async () => {
    if (!selectedCalendlyEt || !calendlyLocalDatetime) return;
    setCalendlySubmitting(true);
    setCalendlySubmitError('');
    try {
      const slotTime = coachLocalToUtcIso(calendlyLocalDatetime, coachTimezone);
      const res = await fetch(`${config.apiBaseUrl}/book/${coachId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
        body: JSON.stringify({
          event_type_id: selectedCalendlyEt.uri,
          slot_time: slotTime,
          client_email: client.email,
          client_name: client.name,
          meeting_name: calendlyMeetingName.trim() || selectedCalendlyEt.name,
          invitee_timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        }),
      });
      const data = await res.json();
      if (!res.ok) { setCalendlySubmitError(data.detail || 'Booking failed'); return; }
      const rateVal = parseFloat(calendlyRate);
      if (data.appointment_id && !isNaN(rateVal) && rateVal > 0) {
        try { await setAppointmentPricing(data.appointment_id, rateVal, calendlyCurrency); } catch { /* non-fatal */ }
      }
      onSessionCreated(data.meeting_url || undefined);
      setCalendlyBookedInfo({
        name: calendlyMeetingName.trim() || selectedCalendlyEt.name,
        slot: slotTime,
        duration: selectedCalendlyEt.duration_minutes,
        meetUrl: data.meeting_url || undefined,
      });
    } catch {
      setCalendlySubmitError('Failed to create session');
    } finally {
      setCalendlySubmitting(false);
    }
  };

  const calendlyOptions: EventTypeOption[] =
    (calendlySelectedUris.length > 0
      ? calendlyEventTypes.filter(et => calendlySelectedUris.includes(et.uri))
      : calendlyEventTypes
    ).map(et => ({
      uri: et.uri,
      scheduling_url: et.scheduling_url,
      name: et.name,
      duration_minutes: et.duration,
      description: et.video_configured ? 'Video included' : undefined,
    }));

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-dark-canvas">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-3 border-b border-dark-border bg-dark-sidebar flex-shrink-0">
        <h2 className="font-semibold text-white text-sm">
          {reschedule ? 'Reschedule Session' : 'Schedule Session'} — <span className="text-primary-300">{client.name}</span>
        </h2>
        <button onClick={onClose} className="text-gray-400 hover:text-white transition-colors">
          <XMarkIcon className="w-5 h-5" />
        </button>
      </div>

      {/* AI consent declined warning */}
      {client.ai_consent === false && (
        <div className="px-6 py-3 bg-red-950/60 border-b border-red-800 flex-shrink-0">
          <p className="text-sm text-red-300">
            <span className="font-semibold">Client has declined the AI usage policy.</span>{' '}
            Notes will not be generated for sessions and the coach is responsible for manually capturing notes.
          </p>
        </div>
      )}

      {schedulingType === 'google' && (
        <GoogleSchedulePanel
          client={client}
          coachId={coachId}
          authToken={authToken}
          eventTypes={googleEventTypes}
          eventTypesLoading={googleEventTypesLoading}
          existingEvents={existingEvents}
          coachTimezone={coachTimezone}
          onSessionCreated={onSessionCreated}
          onClose={onClose}
          onCancelAppointment={onCancelAppointment}
          reschedule={reschedule}
          currencies={currencies}
          coachCurrency={coachCurrency}
        />
      )}

      {/* Calendly cannot reschedule via API — cancel + rebook in Calendly instead */}
      {schedulingType === 'calendly' && reschedule && (
        <div className="flex-1 flex items-center justify-center p-8">
          <p className="text-sm text-gray-400 max-w-sm text-center">
            This session was booked through Calendly, which doesn't support rescheduling from CoachSight.
            Please reschedule it in Calendly, or cancel it here and book a new time.
          </p>
        </div>
      )}

      {schedulingType === 'calendly' && !reschedule && !calendlyBookedInfo && calendlyStep === 'types' && (
        <div className="flex-1 overflow-y-auto p-8">
          <p className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-4">Session Type</p>
          {calendlyEventTypesLoading ? (
            <div className="animate-spin w-5 h-5 border-2 border-primary-500 border-t-transparent rounded-full" />
          ) : calendlyOptions.length === 0 ? (
            <p className="text-sm text-gray-400">No session types configured in Calendly.</p>
          ) : (
            <div className="space-y-3 max-w-sm">
              {calendlyOptions.map((et, i) => (
                <button
                  key={et.uri ?? i}
                  onClick={() => { setSelectedCalendlyEt(et); setCalendlyMeetingName(et.name); setCalendlyStep('slots'); }}
                  className="w-full text-left bg-dark-card border border-dark-border hover:border-primary-500 rounded-xl px-5 py-4 transition-colors group"
                >
                  <p className="text-white font-medium text-sm group-hover:text-primary-300 transition-colors">{et.name}</p>
                  <p className="text-xs text-gray-400 mt-0.5 flex items-center gap-1">
                    <ClockIcon className="w-3.5 h-3.5 inline" />
                    {et.duration_minutes} min
                    {et.description && <span className="ml-2 text-gray-500">· {et.description}</span>}
                  </p>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {schedulingType === 'calendly' && !reschedule && !calendlyBookedInfo && calendlyStep === 'slots' && selectedCalendlyEt && (
        <SlotPicker
          coachId={coachId}
          eventTypeId={selectedCalendlyEt.uri ?? ''}
          durationMinutes={selectedCalendlyEt.duration_minutes}
          authToken={authToken}
          onSelect={slot => { setCalendlyLocalDatetime(toDatetimeLocal(slot, coachTimezone)); setCalendlyStep('confirm'); }}
          onBack={() => setCalendlyStep('types')}
        />
      )}

      {schedulingType === 'calendly' && !reschedule && !calendlyBookedInfo && calendlyStep === 'confirm' && selectedCalendlyEt && (
        <div className="flex-1 overflow-y-auto p-6 max-w-sm mx-auto w-full space-y-4">
          <button onClick={() => setCalendlyStep('slots')} className="text-xs text-gray-400 hover:text-gray-200 transition-colors">
            ← Back to time selection
          </button>
          <div>
            <label className="block text-xs font-medium text-gray-400 mb-1">Date & Time</label>
            <input
              type="datetime-local"
              value={calendlyLocalDatetime}
              onChange={e => setCalendlyLocalDatetime(e.target.value)}
              className="w-full border border-dark-border rounded-lg p-2 text-sm bg-dark-canvas text-gray-200 focus:ring-1 focus:ring-primary-500 focus:outline-none"
            />
            <p className="text-xs text-gray-600 mt-1">{selectedCalendlyEt.duration_minutes} min · {selectedCalendlyEt.name}</p>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-400 mb-1">Session Name</label>
            <input
              type="text"
              value={calendlyMeetingName}
              onChange={e => setCalendlyMeetingName(e.target.value)}
              placeholder={selectedCalendlyEt.name}
              className="w-full border border-dark-border rounded-lg p-2 text-sm bg-dark-canvas text-gray-200 placeholder-dark-muted focus:ring-1 focus:ring-primary-500 focus:outline-none"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-400 mb-1">Rate <span className="text-dark-muted font-normal">(optional)</span></label>
            <div className="flex gap-2">
              <input
                type="number" min="0" step="0.01" value={calendlyRate}
                onChange={e => setCalendlyRate(e.target.value)} placeholder="150.00"
                className="flex-1 border border-dark-border rounded-lg p-2 text-sm bg-dark-canvas text-gray-200 placeholder-dark-muted focus:ring-1 focus:ring-primary-500 focus:outline-none"
              />
              <select value={calendlyCurrency} onChange={e => setCalendlyCurrency(e.target.value)}
                className="border border-dark-border rounded-lg p-2 text-sm bg-dark-canvas text-gray-200 focus:outline-none">
                {currencies.map(c => (
                  <option key={c.code} value={c.code}>{c.name}</option>
                ))}
              </select>
            </div>
          </div>
          {calendlySubmitError && (
            <p className="text-xs text-red-400 bg-red-900/20 rounded-lg px-3 py-2">{calendlySubmitError}</p>
          )}
          <button
            onClick={handleCalendlyConfirm}
            disabled={calendlySubmitting || !calendlyLocalDatetime}
            className="w-full py-2.5 rounded-xl text-sm font-semibold text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            {calendlySubmitting ? 'Creating…' : 'Create Session'}
          </button>
        </div>
      )}

      {schedulingType === 'calendly' && !reschedule && calendlyBookedInfo && (
        <div className="flex-1 flex items-center justify-center p-8">
          <div className="w-full max-w-sm space-y-4">
            <div className="p-4 bg-green-900/30 border border-green-700/40 rounded-xl text-center">
              <CheckCircleIcon className="w-8 h-8 text-green-400 mx-auto mb-2" />
              <p className="text-sm font-semibold text-green-400">Session booked!</p>
              <p className="text-xs text-gray-300 mt-1 font-medium">{calendlyBookedInfo.name}</p>
              <p className="text-xs text-gray-500 mt-0.5">
                {new Date(calendlyBookedInfo.slot).toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' })}
                {' · '}{formatTime(calendlyBookedInfo.slot)}
                {' · '}{calendlyBookedInfo.duration} min
              </p>
              {calendlyBookedInfo.meetUrl && (
                <p className="text-xs text-green-400 mt-1">
                  Meet link: <a href={calendlyBookedInfo.meetUrl} target="_blank" rel="noreferrer" className="underline">{calendlyBookedInfo.meetUrl}</a>
                </p>
              )}
            </div>
            <button
              onClick={() => { setCalendlyBookedInfo(null); setCalendlyStep('types'); setSelectedCalendlyEt(null); setCalendlyLocalDatetime(''); setCalendlyMeetingName(''); }}
              className="w-full py-2.5 rounded-xl text-sm font-semibold text-white bg-primary-600 hover:bg-primary-700 transition-colors"
            >
              Schedule another session
            </button>
            <button onClick={onClose} className="w-full py-2 rounded-xl text-sm font-medium text-gray-400 hover:text-white border border-dark-border hover:border-gray-500 transition-colors">
              Done
            </button>
          </div>
        </div>
      )}

      {schedulingType === 'none' && (
        <div className="flex-1 flex items-center justify-center">
          <p className="text-sm text-gray-400">No scheduling integration configured. Go to Settings → Scheduling.</p>
        </div>
      )}
    </div>
  );
};

export default ScheduleSessionModal;
